/**
 * useClipboard - Clipboard functionality hook with modern API support
 */

import { useState, useCallback, useEffect } from 'react';
import { logInfo, logError } from '../utils/log';

export const useClipboard = (text, resetDelay = 2000) => {
  const [tooltipText, setTooltipText] = useState('Copy Address');
  const [isSupported, setIsSupported] = useState(false);

  // Check clipboard API support
  useEffect(() => {
    setIsSupported(
      navigator.clipboard && 
      typeof navigator.clipboard.writeText === 'function'
    );
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (!text) {
      logError('No text provided to copy');
      return false;
    }

    try {
      if (isSupported) {
        // Modern Clipboard API
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (!successful) {
          throw new Error('Fallback copy failed');
        }
      }

      setTooltipText('Copied!');
      logInfo(`Text copied to clipboard: ${text.substring(0, 10)}...`);

      // Reset tooltip after delay
      setTimeout(() => {
        setTooltipText('Copy Address');
      }, resetDelay);

      return true;
    } catch (error) {
      logError('Failed to copy to clipboard:', error);
      setTooltipText('Copy failed');
      
      // Reset tooltip after delay
      setTimeout(() => {
        setTooltipText('Copy Address');
      }, resetDelay);

      return false;
    }
  }, [text, isSupported, resetDelay]);

  const resetTooltip = useCallback(() => {
    setTooltipText('Copy Address');
  }, []);

  return {
    copyToClipboard,
    tooltipText,
    isSupported,
    resetTooltip,
  };
};
